import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { mAgoraLinha, type LinhaM } from '@/lib/mParqueVivo';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Smartphone, Phone, AlertCircle, Wifi, MapPin, HardDrive, TrendingUp, PhoneCall, Calendar, CircleDollarSign, Tag, Copy, ArrowRightLeft } from 'lucide-react';
import { useParqueMovelByCnpj, useParqueFixoByCnpj, useOperadoraDasLinhas, saiuDaVivo } from '@/hooks/useParqueVivoByCnpj';
import { formatarMoeda, formatarTelefone } from '@/utils/formatters';
import { ClickToCall } from '@/components/common/ClickToCall';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface LeadParqueVivoTabProps {
  cnpj: string | null | undefined;
  leadId?: string;
}

// Interface para agrupamento por conta
interface ContaAgrupada {
  conta_cobranca: string;
  linhas: string[];
  internet: {
    produto: string | null;
    tecnologia: string | null;
    designador: string | null;
    ds_tipo_rede_2: string | null;
  } | null;
  valor_total: number;
  m: number | null;
  dt_ini_fdlz: string | null;
  dt_fim_fdlz: string | null;
  flg_recomendacao: string | null;
  svas: { nome: string; valor: number }[];
  deparas: string[];
  endereco: {
    logradouro: string | null;
    numero: string | null;
    bairro: string | null;
    cidade: string | null;
    uf: string | null;
    complemento: string | null;
  };
}

// Formata data YYYY-MM-DD para DD/MM/YYYY
function formatDateBR(date: string | null): string {
  if (!date) return '?';
  if (date.includes('/')) return date;
  const parts = date.split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return date;
}

// Função para verificar se fidelidade está ativa
function isFidelidadeAtiva(dtFim: string | null): boolean {
  if (!dtFim) return false;
  let dataFim: Date;
  if (dtFim.includes('/')) {
    const [dia, mes, ano] = dtFim.split('/');
    dataFim = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
  } else {
    dataFim = new Date(dtFim);
  }
  return dataFim > new Date();
}

// Função para agrupar serviços por conta
function agruparPorConta(servicos: Array<{
  id: string;
  cnpj: string;
  conta_cobranca: string;
  ds_produto: string;
  tecnologia_origem: string | null;
  vl_fat_bruto: number | null;
  m: number | null;
  designador: string | null;
  ds_tipo_produto: string | null;
  endereco: string | null;
  numero: string | null;
  bairro: string | null;
  ds_municipio_origem: string | null;
  uf: string | null;
  cep: string | null;
  rede: string | null;
  dt_ini_fdlz: string | null;
  dt_fim_fdlz: string | null;
  ds_tipo_rede_2: string | null;
  depara: string | null;
  flg_sva: number | null;
  ds_produto_tecnologia: string | null;
  flg_recomendacao: string | null;
  complemento: string | null;
  ds_end_prefixo: string | null;
}>): ContaAgrupada[] {
  const contas = new Map<string, ContaAgrupada>();
  
  for (const s of servicos) {
    const chave = s.conta_cobranca || 'sem_conta';
    
    if (!contas.has(chave)) {
      contas.set(chave, {
        conta_cobranca: s.conta_cobranca,
        linhas: [],
        internet: null,
        valor_total: 0,
        m: s.m,
        dt_ini_fdlz: s.dt_ini_fdlz,
        dt_fim_fdlz: s.dt_fim_fdlz,
        flg_recomendacao: s.flg_recomendacao,
        svas: [],
        deparas: [],
        endereco: {
          logradouro: s.endereco,
          numero: s.numero,
          bairro: s.bairro,
          cidade: s.ds_municipio_origem,
          uf: s.uf,
          complemento: s.complemento,
        }
      });
    }
    
    const conta = contas.get(chave)!;
    
    // Somar valor
    conta.valor_total += s.vl_fat_bruto || 0;
    
    // Capturar fidelidade (pegar a primeira não-nula)
    if (!conta.dt_ini_fdlz && s.dt_ini_fdlz) conta.dt_ini_fdlz = s.dt_ini_fdlz;
    if (!conta.dt_fim_fdlz && s.dt_fim_fdlz) conta.dt_fim_fdlz = s.dt_fim_fdlz;
    
    // Capturar recomendação
    if (s.flg_recomendacao === 'SIM') conta.flg_recomendacao = 'SIM';
    
    // Detectar internet (DS_TIPO_PRODUTO = 'DADOS')
    if (s.ds_tipo_produto === 'DADOS' && !conta.internet) {
      conta.internet = {
        produto: s.ds_produto,
        tecnologia: s.tecnologia_origem,
        designador: s.designador,
        ds_tipo_rede_2: s.ds_tipo_rede_2,
      };
    }
    
    // Coletar SVAs
    if (s.flg_sva === 1 && s.depara) {
      const nomeExibicao = s.ds_produto || s.depara;
      if (!conta.svas.find(sva => sva.nome === nomeExibicao)) {
        conta.svas.push({ nome: nomeExibicao, valor: s.vl_fat_bruto || 0 });
      }
    }
    
    // Coletar DEPARAs únicos
    if (s.depara && !conta.deparas.includes(s.depara)) {
      conta.deparas.push(s.depara);
    }
    
    // Coletar linhas telefônicas
    if (s.designador && /^\d{10,11}$/.test(s.designador)) {
      if (!conta.linhas.includes(s.designador)) {
        conta.linhas.push(s.designador);
      }
    }
  }
  
  return Array.from(contas.values())
    .sort((a, b) => b.valor_total - a.valor_total);
}

// Badge helper para DEPARA
function getDeparaBadgeVariant(depara: string): 'default' | 'secondary' | 'outline' | 'warning' {
  if (depara.includes('PACOTE')) return 'default';
  if (depara.includes('SVA')) return 'warning';
  if (depara.includes('BDL')) return 'secondary';
  return 'outline';
}

export function LeadParqueVivoTab({ cnpj, leadId }: LeadParqueVivoTabProps) {
  const { data: parqueMovel, isLoading: loadingMovel } = useParqueMovelByCnpj(cnpj);
  // Quem cada linha e' HOJE, pela consulta de numero. A base diz "Vivo" porque
  // era Vivo na data da carga; isto desmente quando ja' nao e'.
  const { data: operadoras } = useOperadoraDasLinhas((parqueMovel ?? []).map((l) => l.nr_telefone));
  const opDaLinha = (tel?: string | null) =>
    operadoras?.get(`55${String(tel || '').replace(/\D/g, '')}`);
  const { data: parqueFixo, isLoading: loadingFixo } = useParqueFixoByCnpj(cnpj);

  const loading = loadingMovel || loadingFixo;
  const temMovel = parqueMovel && parqueMovel.length > 0;
  const temFixo = parqueFixo && parqueFixo.length > 0;
  const semDados = !loading && !temMovel && !temFixo;

  // M envelhecido: régua única em lib/mParqueVivo (M da carga + meses desde a carga)
  const mAtualLinha = (l: LinhaM): number | null => mAgoraLinha(l);
  const mDisplay = (l: any): string => {
    const v = mAtualLinha(l);
    return v == null ? '-' : (v >= 24 ? '24+' : String(v));
  };

  // Calcular totais móvel
  const totalFaturaMovel = parqueMovel?.reduce((sum, l) => sum + (l.fat_medio_03_meses || 0), 0) || 0;
  const linhasEstimadas = (l: any): number => {
    if (l.nr_telefone) return 1;
    const mgb = /(\d+(?:\.\d+)?)\s*GB/i.exec(l.plano || '');
    const planGb = mgb ? parseFloat(mgb[1]) : null;
    if (planGb && planGb >= 1 && l.qtd_gb_contratado_dados > 0) {
      const k = Math.round(l.qtd_gb_contratado_dados / planGb);
      if (k >= 2 && Math.abs(l.qtd_gb_contratado_dados / planGb - k) < 0.01) return k;
    }
    return 1;
  };
  const totalLinhasMovel = parqueMovel?.reduce((sum, l) => sum + linhasEstimadas(l), 0) || 0;
  const saidas = (parqueMovel ?? []).filter((l) => saiuDaVivo(opDaLinha(l.nr_telefone)?.operadora)).length;
  const totalGBContratado = parqueMovel?.reduce((sum, l) => sum + (l.qtd_gb_contratado_dados || 0), 0) || 0;
  const totalGBTrafegado = parqueMovel?.reduce((sum, l) => sum + (l.qtd_gb_traf_dados || 0), 0) || 0;
  const percentualUso = totalGBContratado > 0 ? Math.round((totalGBTrafegado / totalGBContratado) * 100) : 0;
  const totalSemMulta = parqueMovel?.reduce((sum, l) => sum + ((mAtualLinha(l) ?? 0) >= 24 ? linhasEstimadas(l) : 0), 0) || 0;

  const copiarTelefones = () => {
    const nums = (parqueMovel || []).map(l => formatarTelefone(l.nr_telefone || '')).filter(Boolean).join('\n');
    if (!nums) return;
    navigator.clipboard?.writeText(nums)
      .then(() => toast.success(`${totalLinhasMovel} telefone(s) copiado(s)`))
      .catch(() => toast.error('Não foi possível copiar'));
  };

  // Agrupar serviços fixos por conta
  const contasAgrupadas = temFixo ? agruparPorConta(parqueFixo) : [];
  const totalFaturaFixo = contasAgrupadas.reduce((sum, c) => sum + c.valor_total, 0);
  const totalContas = contasAgrupadas.length;
  const totalLinhasFixas = contasAgrupadas.reduce((sum, c) => sum + c.linhas.length, 0);
  const totalLinksInternet = contasAgrupadas.filter(c => c.internet).length;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (semDados) {
    return (
      <p className="text-xs text-muted-foreground text-center py-3">Nenhum dado encontrado para este CNPJ.</p>
    );
  }

  return (
    <div className="space-y-4">
      {/* PARQUE MÓVEL */}
      {temMovel && (
        <Card>
          <CardHeader className="border-b border-border px-4 py-0">
            <div className="flex h-[34px] items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-foreground">
                <Smartphone className="h-3.5 w-3.5 text-muted-foreground" />
                Linhas móveis Vivo
              </CardTitle>
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="text-xs">
                  {totalLinhasMovel} {totalLinhasMovel === 1 ? 'linha' : 'linhas'}
                </Badge>
                {/* Quantas ja' sairam — a informacao que muda a conversa ANTES
                    de o consultor descer a tabela. So' aparece se houver. */}
                {saidas > 0 && (
                  <Badge
                    variant="outline"
                    className="text-xs gap-1 font-normal text-muted-foreground"
                    title={`${saidas} de ${totalLinhasMovel} linha(s) ja nao sao mais da Vivo`}
                  >
                    <ArrowRightLeft className="h-3 w-3" />
                    {saidas} ja saiu{saidas > 1 ? 'ram' : ''}
                  </Badge>
                )}
                {totalSemMulta > 0 && (
                  <Badge variant="default" className="text-xs bg-success text-white">
                    {totalSemMulta} s/ multa (M≥24)
                  </Badge>
                )}
                <span className="text-sm font-semibold text-primary">
                  {formatarMoeda(totalFaturaMovel)}
                </span>
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs gap-1" onClick={copiarTelefones}>
                  <Copy className="h-3 w-3" /> Copiar números
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 px-4 pb-4">
            {/* Cards de resumo GB */}
            <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                ['Linhas', String(totalLinhasMovel), ''],
                ['Fatura total', formatarMoeda(totalFaturaMovel), '/mês'],
                ['Contratado', `${totalGBContratado} GB`, ''],
                ['Uso real', `${totalGBTrafegado} GB`, `· ${percentualUso}%`],
              ].map(([rotulo, valor, sufixo]) => (
                <div key={rotulo} className="rounded-lg border border-border bg-muted/40 px-3 py-2">
                  <p className="truncate font-display text-[17px] font-bold leading-tight tabular-nums text-primary" title={`${valor}${sufixo ? ` ${sufixo}` : ''}`}>
                    {valor}{sufixo && <span className="ml-1 text-[10.5px] font-semibold text-muted-foreground">{sufixo}</span>}
                  </p>
                  <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{rotulo}</p>
                </div>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr className="text-[8.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
                    <th className="p-2 text-left font-bold">Linha</th>
                    <th className="p-2 text-left font-bold">Plano</th>
                    <th className="p-2 text-left font-bold" title="Operadora da linha HOJE, pela consulta de número — quando não há consulta, vale a base (Vivo)">Oper.</th>
                    <th className="p-2 text-right font-bold">Fatura</th>
                    <th className="p-2 text-center font-bold" title="Meses de fidelização da linha — M≥24 ou não fidelizada = sem multa">M</th>
                    <th className="p-2 text-center font-bold">GB contr.</th>
                    <th className="p-2 text-center font-bold">GB usado</th>
                    <th className="p-2 text-left font-bold">Aparelho</th>
                    <th className="p-2 text-center font-bold">Serasa</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {parqueMovel.map((linha) => {
                    const gbContratado = linha.qtd_gb_contratado_dados || 0;
                    const gbUsado = linha.qtd_gb_traf_dados || 0;
                    const usoPercent = gbContratado > 0 ? Math.round((gbUsado / gbContratado) * 100) : 0;
                    
                    return (
                      <tr key={linha.id} className="hover:bg-muted/30">
                        <td className="whitespace-nowrap p-2 font-bold tabular-nums text-foreground">
                          {!linha.nr_telefone ? (
                            <Badge variant="secondary" className="text-[10px] font-sans" title="A operadora entregou este grupo somado — números individuais não vieram na base; valores e GB são o total do grupo">
                              grupo de ~{linhasEstimadas(linha)} linhas
                            </Badge>
                          ) : (
                          <ClickToCall 
                            telefone={linha.nr_telefone || ''} 
                            leadId={leadId}
                            className="inline-flex items-center gap-1 hover:text-primary transition-colors cursor-pointer"
                          >
                            {formatarTelefone(linha.nr_telefone || '')}
                            <PhoneCall className="h-3 w-3 text-muted-foreground" />
                          </ClickToCall>
                          )}
                        </td>
                        <td className="p-2 max-w-[200px] truncate" title={linha.plano || ''}>
                          {linha.plano || '—'}
                        </td>
                        {(() => {
                          const op = opDaLinha(linha.nr_telefone);
                          const saiu = saiuDaVivo(op?.operadora);
                          const quando = op?.portouEm ? String(op.portouEm).slice(0, 10).split('-').reverse().join('/') : null;
                          return (
                            <td
                              className={`p-2 text-[10px] font-bold uppercase tracking-wide ${saiu ? 'text-primary dark:text-[#C87484]' : 'text-muted-foreground'}`}
                              title={saiu
                                ? `Consulta de número: esta linha não é mais da Vivo — hoje é ${op?.operadora}${quando ? ` (portada em ${quando})` : ''}`
                                : op?.operadora
                                  ? 'Consulta de número confirma: linha segue na Vivo'
                                  : 'Linha da base Vivo — ainda sem consulta de número'}
                            >
                              {saiu ? op?.operadora : 'VIVO'}
                            </td>
                          );
                        })()}
                        <td className="p-2 text-right font-medium tabular-nums">
                          {linha.fat_medio_03_meses ? formatarMoeda(linha.fat_medio_03_meses) : '—'}
                        </td>
                        {(() => {
                          const semMulta = linha.m === 999 || linha.fidelizado === 'Não Fidelizado' || (mAtualLinha(linha) ?? 0) >= 24;
                          return (
                            <td
                              className={`p-2 text-center font-semibold tabular-nums ${semMulta ? 'text-foreground' : 'text-primary dark:text-[#C87484]'}`}
                              title={linha.m === 999 || linha.fidelizado === 'Não Fidelizado'
                                ? 'Não fidelizada — sem multa'
                                : `${semMulta ? 'Sem multa (M≥24)' : 'Fidelização vigente — multa até M24'}${mAtualLinha(linha) != null && linha.m != null && mAtualLinha(linha) !== linha.m ? ` · base M${linha.m} + meses desde a carga` : ''}`}
                            >
                              {mDisplay(linha)}
                            </td>
                          );
                        })()}
                        <td className="p-2 text-center tabular-nums">
                          {gbContratado > 0 ? `${gbContratado}` : '—'}
                        </td>
                        <td className="p-2 text-center tabular-nums">
                          {gbUsado > 0 ? (
                            <span className={usoPercent > 100 ? 'font-bold text-primary dark:text-[#C87484]' : ''} title={usoPercent > 100 ? 'Franquia estourada — gancho de renovação' : undefined}>
                              {gbUsado} GB
                              <span className={`ml-1 text-[10px] ${usoPercent > 100 ? 'font-bold' : 'text-muted-foreground'}`}>· {usoPercent}%</span>
                            </span>
                          ) : '—'}
                        </td>
                        <td className="p-2 text-muted-foreground max-w-[150px] truncate" title={linha.aparelho_trafego_modelo || ''}>
                          {linha.aparelho_trafego_modelo || '—'}
                        </td>
                        <td className="p-2 text-center">
                          {(() => {
                            const s = (linha.semaforo_serasa || '').toUpperCase();
                            if (!s) return <span className="text-muted-foreground">—</span>;
                            const cor = s === 'VERDE' ? 'bg-success' : (s === 'VERMELHO' || s === 'PRETO') ? 'bg-danger' : s === 'AMARELO' ? 'bg-warning' : 'bg-muted-foreground';
                            return (
                              <span className="inline-flex items-center gap-1" title={linha.mensagem_retorno_serasa || s}>
                                <span className={`inline-block h-2.5 w-2.5 rounded-full ${cor}`} />
                                <span className="text-[10.5px] text-muted-foreground">{s.toLowerCase()}</span>
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[10px] text-muted-foreground">uso acima de 100% em bordô é o gancho de renovação · — = a operadora não retornou o dado · M = meses de fidelização (M≥24 ou não fidelizada = sem multa)</p>
          </CardContent>
        </Card>
      )}

      {/* PARQUE FIXO — a fixa no MESMO molde da móvel (doc base-vivo-fixa):
          zero azul, zero emoji, filete vinho, M na regra da móvel */}
      {temFixo && (
        <Card>
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.09em] text-card-foreground">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                Serviços Fixos Vivo
              </CardTitle>
              <div className="flex items-baseline gap-2">
                <span className="text-[10px] text-muted-foreground">{totalContas} {totalContas === 1 ? 'conta' : 'contas'}</span>
                <span className="font-display text-[13px] font-bold tabular-nums text-money-highlight">
                  {formatarMoeda(totalFaturaFixo)}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0 px-4 pb-4 space-y-3">
            {/* Tiles do molde: lucide 11px muted; só o Fatura Total tem cor (money) */}
            <div className="mb-2 grid grid-cols-4 gap-2">
              <div className="rounded-[9px] border border-[#E7DCCB] bg-card p-2 text-center dark:border-border">
                <Phone className="mx-auto mb-1 h-[11px] w-[11px] text-muted-foreground" />
                <p className="font-display text-[16px] font-bold tabular-nums text-card-foreground">{totalContas}</p>
                <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Contas</p>
              </div>
              <div className="rounded-[9px] border border-[#E7DCCB] bg-card p-2 text-center dark:border-border">
                <CircleDollarSign className="mx-auto mb-1 h-[11px] w-[11px] text-muted-foreground" />
                <p className="font-display text-[16px] font-bold tabular-nums text-money-highlight">{formatarMoeda(totalFaturaFixo)}</p>
                <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Fatura Total</p>
              </div>
              <div className="rounded-[9px] border border-[#E7DCCB] bg-card p-2 text-center dark:border-border">
                <PhoneCall className="mx-auto mb-1 h-[11px] w-[11px] text-muted-foreground" />
                <p className="font-display text-[16px] font-bold tabular-nums text-card-foreground">{totalLinhasFixas}</p>
                <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Linhas Fixas</p>
              </div>
              <div className="rounded-[9px] border border-[#E7DCCB] bg-card p-2 text-center dark:border-border">
                <Wifi className="mx-auto mb-1 h-[11px] w-[11px] text-muted-foreground" />
                <p className="font-display text-[16px] font-bold tabular-nums text-card-foreground">{totalLinksInternet}</p>
                <p className="text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Banda Larga</p>
              </div>
            </div>

            {contasAgrupadas.map((conta) => {
              const fidelidadeAtiva = isFidelidadeAtiva(conta.dt_fim_fdlz);
              // Regra da móvel: multa só com fidelidade ATIVA e M<24 — aí o M vai a bordô 700
              const multaAtiva = fidelidadeAtiva && conta.m != null && conta.m < 24;

              return (
                <Card key={conta.conta_cobranca} className="relative overflow-hidden rounded-r-[11px] rounded-l-none border border-border bg-card pl-1">
                  <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]" />
                  <CardContent className="space-y-2 p-3">
                    {/* Header da Conta */}
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11.5px] font-bold tabular-nums text-card-foreground">
                        Conta {conta.conta_cobranca}
                      </span>
                      {conta.flg_recomendacao === 'SIM' && (
                        <span className="flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-[0.6px] text-[#075E54] dark:text-[#25D366]">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#075E54] dark:bg-[#25D366]" />
                          recomendada
                        </span>
                      )}
                      <span
                        className={`ml-auto flex h-[18px] items-center rounded-full bg-[#F2EEE8] px-2 text-[9.5px] font-bold tabular-nums dark:bg-white/[0.06] ${multaAtiva ? 'text-[#7A2235] dark:text-[#C87484]' : 'text-muted-foreground'}`}
                        title={multaAtiva ? `M${conta.m} com fidelidade ativa — multa em caso de portabilidade` : 'M≥24 ou não fidelizada = sem multa'}
                      >
                        M {conta.m ?? '—'}
                      </span>
                    </div>

                    {/* Fidelidade */}
                    {(conta.dt_ini_fdlz || conta.dt_fim_fdlz) && (
                      <div className="flex items-center gap-2 text-xs">
                        <Calendar className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                        <span className="text-muted-foreground">Fidelidade:</span>
                        <span className="font-medium tabular-nums">{formatDateBR(conta.dt_ini_fdlz)}</span>
                        <span className="text-muted-foreground">até</span>
                        <span className="font-medium tabular-nums">{formatDateBR(conta.dt_fim_fdlz)}</span>
                        <span className={`flex items-center gap-1 text-[9.5px] font-bold uppercase ${fidelidadeAtiva ? 'text-[#075E54] dark:text-[#25D366]' : 'text-muted-foreground'}`}>
                          <span className={`h-1.5 w-1.5 rounded-full ${fidelidadeAtiva ? 'bg-[#075E54] dark:bg-[#25D366]' : 'bg-muted-foreground/40'}`} />
                          {fidelidadeAtiva ? 'ativa' : 'vencida'}
                        </span>
                      </div>
                    )}

                    {/* Internet */}
                    <div className="flex items-center gap-2 text-sm">
                      <Wifi className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                      {conta.internet ? (
                        <span className="flex flex-wrap items-center gap-1.5 text-xs">
                          <strong className="text-[12px] font-bold text-card-foreground">{conta.internet.produto}</strong>
                          {conta.internet.tecnologia && (
                            <span className="flex h-[18px] items-center rounded-full bg-[#F2EEE8] px-2 text-[9.5px] font-bold text-muted-foreground dark:bg-white/[0.06]">
                              {conta.internet.tecnologia}
                            </span>
                          )}
                          {conta.internet.ds_tipo_rede_2 && (
                            <span className="flex h-[18px] items-center rounded-full bg-[#F2EEE8] px-2 text-[9.5px] font-bold text-muted-foreground dark:bg-white/[0.06]">
                              {conta.internet.ds_tipo_rede_2}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Sem internet</span>
                      )}
                    </div>
                    
                    {/* Linhas telefônicas */}
                    {conta.linhas.length > 0 && (
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <Phone className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                        {conta.linhas.map((telefone, idx) => (
                          <ClickToCall 
                            key={idx}
                            telefone={telefone} 
                            leadId={leadId}
                            className="font-mono text-xs hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-0.5"
                          >
                            {formatarTelefone(telefone)}
                            <PhoneCall className="h-2.5 w-2.5 text-muted-foreground" />
                          </ClickToCall>
                        ))}
                      </div>
                    )}
                    
                    {/* DEPARAs como badges */}
                    {conta.deparas.length > 0 && (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Tag className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        {conta.deparas.map((dep, idx) => (
                          <Badge 
                            key={idx} 
                            variant={getDeparaBadgeVariant(dep)} 
                            className="text-[9px] px-1.5 py-0"
                          >
                            {dep}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* SVAs — dívida embutida na conta: bordô, nunca âmbar */}
                    {conta.svas.length > 0 && (
                      <div className="space-y-1 rounded border border-border bg-muted/30 p-2">
                        <p className="text-[10px] font-bold uppercase tracking-[0.06em] text-[#7A2235] dark:text-[#C87484]">SVAs identificados</p>
                        {conta.svas.map((sva, idx) => (
                          <div key={idx} className="flex justify-between text-xs">
                            <span className="max-w-[250px] truncate text-muted-foreground">{sva.nome}</span>
                            <span className="font-bold tabular-nums text-[#7A2235] dark:text-[#C87484]">{formatarMoeda(sva.valor)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Endereço */}
                    {(conta.endereco.logradouro || conta.endereco.cidade) && (
                      <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-1">
                          {[
                            conta.endereco.logradouro,
                            conta.endereco.numero,
                            conta.endereco.complemento,
                            conta.endereco.bairro,
                            conta.endereco.cidade,
                            conta.endereco.uf
                          ].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}
                    
                    {/* Valor Total */}
                    <div className="flex justify-end border-t border-border/50 pt-1">
                      <span className="font-display text-[13px] font-bold tabular-nums text-money-highlight">
                        {formatarMoeda(conta.valor_total)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* RESUMO TOTAL — só quando há móvel E fixo (com um parque só, o tile do card já diz) */}
      {temMovel && temFixo && (
        <Card className="bg-muted/30">
          <CardContent className="px-4 py-2.5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Fatura total estimada · móvel + fixo</span>
              <span className="font-display text-[17px] font-bold tabular-nums text-money-highlight">
                {formatarMoeda(totalFaturaMovel + totalFaturaFixo)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
